'use client';

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogFooter,
  Input,
  Label,
  PageHeader,
  toast,
  cn,
  resolveCargoIcon,
} from '@rr/ui';
import { request } from '@/lib/api/client';
import { IconPicker } from '@/components/IconPicker';

/**
 * Goods catalog admin — platform-global 3-level taxonomy
 * (Category → Subcategory → Good). Three-pane CRUD mirroring the classifiers
 * and reference admin patterns. Calls hit `/platform/goods/*`. Categories and
 * subcategories are global-only; the goods leaf list also surfaces
 * tenant-private goods (free-typed cargo names auto-filed under "Other →
 * Uncategorized"), tagged with the owning tenant via `ownerName`.
 */

type Localized = { en: string | null; ru: string | null; tk: string | null };

interface Row {
  id: string;
  name: string;
  nameLocalized: Localized;
  icon: string | null;
  isActive: boolean;
  sortOrder: number;
  /** Owning tenant for private goods; null/absent for global rows. */
  ownerName?: string | null;
}
interface ListResponse<T> {
  items: T[];
  pagination: { total: number };
}

type Level = 'categories' | 'subcategories' | 'goods';

const emptyName: Localized = { en: '', ru: '', tk: '' };

export default function GoodsCatalogPage() {
  const qc = useQueryClient();
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [subcategoryId, setSubcategoryId] = useState<string | null>(null);

  // ── Queries ───────────────────────────────────────────────────────────
  const categories = useQuery<ListResponse<Row>>({
    queryKey: ['platform', 'goods', 'categories'],
    queryFn: () => request<ListResponse<Row>>('/platform/goods/categories?limit=100'),
  });

  const subcategories = useQuery<ListResponse<Row>>({
    queryKey: ['platform', 'goods', 'subcategories', categoryId],
    enabled: !!categoryId,
    queryFn: () =>
      request<ListResponse<Row>>(`/platform/goods/subcategories?limit=100&categoryId=${categoryId}`),
  });

  const goods = useQuery<ListResponse<Row>>({
    queryKey: ['platform', 'goods', 'goods', subcategoryId],
    enabled: !!subcategoryId,
    queryFn: () =>
      request<ListResponse<Row>>(`/platform/goods/goods?limit=200&subcategoryId=${subcategoryId}`),
  });

  return (
    <div className="space-y-6">
      <PageHeader title="Goods catalog" subtitle="Platform-wide goods taxonomy used by cargo search" />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Pane
          title="Categories"
          level="categories"
          rows={categories.data?.items ?? []}
          isLoading={categories.isLoading}
          selectedId={categoryId}
          onSelect={(id) => {
            setCategoryId(id);
            setSubcategoryId(null);
          }}
          showChevron
          onChanged={() => qc.invalidateQueries({ queryKey: ['platform', 'goods', 'categories'] })}
          buildPayload={(name, icon) => ({ name, icon })}
        />

        <Pane
          title="Subcategories"
          level="subcategories"
          rows={subcategories.data?.items ?? []}
          isLoading={subcategories.isLoading}
          selectedId={subcategoryId}
          onSelect={(id) => setSubcategoryId(id)}
          showChevron
          disabled={!categoryId}
          disabledHint="Select a category"
          onChanged={() =>
            qc.invalidateQueries({ queryKey: ['platform', 'goods', 'subcategories', categoryId] })
          }
          buildPayload={(name, icon) => ({ name, icon, categoryId })}
        />

        <Pane
          title="Goods"
          level="goods"
          rows={goods.data?.items ?? []}
          isLoading={goods.isLoading}
          disabled={!subcategoryId}
          disabledHint="Select a subcategory"
          onChanged={() =>
            qc.invalidateQueries({ queryKey: ['platform', 'goods', 'goods', subcategoryId] })
          }
          buildPayload={(name, icon) => ({ name, icon, categoryId, subcategoryId })}
        />
      </div>
    </div>
  );
}

// ── Pane: one CRUD column for a level ──────────────────────────────────────
interface PaneProps {
  title: string;
  level: Level;
  rows: Row[];
  isLoading: boolean;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  showChevron?: boolean;
  disabled?: boolean;
  disabledHint?: string;
  onChanged: () => void;
  buildPayload: (name: Localized, icon: string | null) => Record<string, unknown>;
}

function Pane({
  title,
  level,
  rows,
  isLoading,
  selectedId,
  onSelect,
  showChevron,
  disabled,
  disabledHint,
  onChanged,
  buildPayload,
}: PaneProps) {
  const [editing, setEditing] = useState<Row | null>(null);
  const [creating, setCreating] = useState(false);

  const remove = useMutation({
    mutationFn: (id: string) => request<void>(`/platform/goods/${level}/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success(`${title.replace(/s$/, '')} removed`);
      onChanged();
    },
    onError: (e: any) => toast.error(e?.message ?? 'Delete failed'),
  });

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>

      <div className="max-h-[60vh] divide-y divide-border overflow-y-auto">
        {disabled ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">{disabledHint}</p>
        ) : isLoading ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">No entries yet</p>
        ) : (
          rows.map((row) => {
            const Icon = resolveCargoIcon(row.icon);
            // Tenant-private goods are owned by a tenant — show who owns them
            // and don't expose platform edit/delete on tenant data.
            const owned = !!row.ownerName;
            return (
              <div
                key={row.id}
                className={cn(
                  'flex items-center gap-2 px-3 py-2',
                  onSelect && 'cursor-pointer hover:bg-muted/50',
                  selectedId === row.id && 'bg-primary/5',
                )}
                onClick={() => onSelect?.(row.id)}
              >
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className={cn('flex-1 truncate text-sm', !row.isActive && 'text-muted-foreground line-through')}>
                  {row.name}
                </span>
                {owned ? (
                  <span className="shrink-0 truncate rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground" title={`Owned by ${row.ownerName}`}>
                    {row.ownerName}
                  </span>
                ) : (
                  <>
                    <button
                      type="button"
                      className="rounded p-1 text-muted-foreground hover:bg-muted"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditing(row);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      className="rounded p-1 text-destructive hover:bg-destructive/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(`Delete "${row.name}"?`)) remove.mutate(row.id);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
                {showChevron && onSelect && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              </div>
            );
          })
        )}
      </div>

      {(creating || editing) && (
        <RowDialog
          title={title.replace(/s$/, '')}
          level={level}
          row={editing}
          buildPayload={buildPayload}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

// ── Create/edit dialog ─────────────────────────────────────────────────────
interface RowDialogProps {
  title: string;
  level: Level;
  row: Row | null;
  buildPayload: (name: Localized, icon: string | null) => Record<string, unknown>;
  onClose: () => void;
  onSaved: () => void;
}

function RowDialog({ title, level, row, buildPayload, onClose, onSaved }: RowDialogProps) {
  const isEdit = !!row;
  const [name, setName] = useState<Localized>(row?.nameLocalized ?? emptyName);
  const [icon, setIcon] = useState<string | null>(row?.icon ?? null);
  const [isActive, setIsActive] = useState(row?.isActive ?? true);

  const save = useMutation({
    mutationFn: () => {
      const payload = { ...buildPayload(name, icon), isActive };
      return isEdit
        ? request(`/platform/goods/${level}/${row!.id}`, { method: 'PATCH', body: JSON.stringify(payload) })
        : request(`/platform/goods/${level}`, { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => {
      toast.success(isEdit ? `${title} updated` : `${title} created`);
      onSaved();
    },
    onError: (e: any) => toast.error(e?.message ?? 'Save failed'),
  });

  const canSave = name.en?.trim() && name.ru?.trim() && name.tk?.trim();

  return (
    <Dialog open onClose={onClose} title={isEdit ? `Edit ${title}` : `New ${title}`}>
      <div className="space-y-4 py-2">
        <div className="grid grid-cols-3 gap-2">
          {(['en', 'ru', 'tk'] as const).map((loc) => (
            <div key={loc} className="space-y-1">
              <Label className="text-xs uppercase text-muted-foreground">{loc}</Label>
              <Input
                value={name[loc] ?? ''}
                onChange={(e) => setName((p) => ({ ...p, [loc]: e.target.value }))}
                placeholder={`Name (${loc})`}
              />
            </div>
          ))}
        </div>

        <div className="space-y-1">
          <Label className="text-xs uppercase text-muted-foreground">Icon</Label>
          <IconPicker value={icon} onChange={setIcon} />
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Active
        </label>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" disabled={!canSave || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
